/**
 * Generic form field component for Kratos UI nodes.
 */

import type { UiNode, UiNodeInputAttributes } from '@ory/client';

interface FormFieldProps {
  node: UiNode;
  disabled?: boolean;
}

export default function FormField({ node, disabled }: FormFieldProps) {
  const attrs = node.attributes as UiNodeInputAttributes;

  // Skip hidden and submit fields
  if (attrs.type === 'hidden') {
    return <input type="hidden" name={attrs.name} value={attrs.value as string} />;
  }

  if (attrs.type === 'submit') {
    return (
      <button
        type="submit"
        name={attrs.name}
        value={attrs.value as string}
        disabled={disabled || attrs.disabled}
        className="btn-primary w-full"
      >
        {node.meta?.label?.text || 'Submit'}
      </button>
    );
  }

  // Get messages for this field
  const messages = node.messages || [];
  const hasError = messages.some((m) => m.type === 'error');

  return (
    <div className="space-y-1">
      {node.meta?.label?.text && (
        <label
          htmlFor={attrs.name}
          className="block text-sm font-medium text-content-primary"
        >
          {node.meta.label.text}
          {attrs.required && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
        </label>
      )}

      <input
        id={attrs.name}
        name={attrs.name}
        type={attrs.type}
        defaultValue={attrs.value as string}
        required={attrs.required}
        disabled={disabled || attrs.disabled}
        autoComplete={attrs.autocomplete}
        placeholder={node.meta?.label?.text}
        className={`input-field ${hasError ? 'border-red-500 dark:border-red-400' : ''}`}
      />

      {messages.map((message, index) => (
        <p
          key={index}
          className={message.type === 'error' ? 'error-message' : 'success-message'}
        >
          {message.text}
        </p>
      ))}
    </div>
  );
}
